import { useState, useRef, useEffect } from 'react'
import { uuid } from '../lib/uuid'
import { useAuth } from '../context/AuthContext'
import { useWedding } from '../context/WeddingContext'
import { supabase } from '../lib/supabase'
import CupidIcon from './CupidIcon'

const OPENING_TEXT = "Hi! I'm Cupid, your Okoiya assistant 👋 Ask me anything about planning your wedding — I'll answer right away. If it's something critical that only our team can sort out, tap “Talk to a human” and a real person will step in."
const LS_TICKET    = 'okoiya_support_ticket'
const LS_MODE      = 'okoiya_support_mode'

export default function ChatWidget() {
  const { user }    = useAuth()
  const { wedding, isPremium } = useWedding()

  // Restore live session from localStorage across refreshes
  const [mode,      setMode]      = useState(() => {
    const s = localStorage.getItem(LS_MODE)
    return (s === 'live' || s === 'ended') ? s : 'intro'
  })
  const [ticketId,  setTicketId]  = useState(() => localStorage.getItem(LS_TICKET) || null)

  const [open,      setOpen]      = useState(false)
  const [messages,  setMessages]  = useState([])
  const [input,     setInput]     = useState('')
  const [unread,    setUnread]    = useState(0)
  const [aiLoading, setAiLoading] = useState(false)

  const bottomRef      = useRef(null)
  const inputRef       = useRef(null)
  const liveChannelRef = useRef(null) // broadcast channel for live mode

  // ── Draggable FAB (same pattern as WisdomFloat) ─────────
  const defaultFabPos = () => ({ left: window.innerWidth - 24 - 56, top: window.innerHeight - 24 - 56 })
  const [fabPos, setFabPos] = useState(defaultFabPos)
  const dragStart  = useRef(null)
  const didDrag    = useRef(false)
  const isTouchTap = useRef(false)

  const clampFab = (left, top) => ({
    left: Math.max(8, Math.min(left, window.innerWidth  - 64)),
    top:  Math.max(8, Math.min(top,  window.innerHeight - 64)),
  })
  function fabMouseDown(e) {
    if (e.button !== 0) return
    didDrag.current = false
    dragStart.current = { x:e.clientX, y:e.clientY, left:fabPos.left, top:fabPos.top }
    e.preventDefault()
  }
  function fabMouseMove(e) {
    if (!dragStart.current) return
    const dx = e.clientX - dragStart.current.x, dy = e.clientY - dragStart.current.y
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDrag.current = true
    setFabPos(clampFab(dragStart.current.left + dx, dragStart.current.top + dy))
  }
  function fabMouseUp() { dragStart.current = null }
  function fabTouchStart(e) {
    didDrag.current = false
    const t = e.touches[0]
    dragStart.current = { x:t.clientX, y:t.clientY, left:fabPos.left, top:fabPos.top }
  }
  function fabTouchMove(e) {
    if (!dragStart.current) return
    const t = e.touches[0]
    const dx = t.clientX - dragStart.current.x, dy = t.clientY - dragStart.current.y
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDrag.current = true
    setFabPos(clampFab(dragStart.current.left + dx, dragStart.current.top + dy))
    e.preventDefault()
  }
  function fabTouchEnd() {
    dragStart.current = null
    if (!didDrag.current) {
      isTouchTap.current = true
      setTimeout(() => { isTouchTap.current = false }, 400)
      handleToggle()
    }
  }
  // Global listeners so dragging continues outside the button
  useEffect(() => {
    window.addEventListener('mousemove', fabMouseMove)
    window.addEventListener('mouseup',   fabMouseUp)
    return () => { window.removeEventListener('mousemove', fabMouseMove); window.removeEventListener('mouseup', fabMouseUp) }
  })
  useEffect(() => {
    const onResize = () => setFabPos(defaultFabPos())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  // Desktop: anchor the chat window above/left of the FAB, clamped on-screen.
  // Mobile keeps the full-width bottom sheet via the CSS media query.
  function windowPos() {
    const W = 340, H = 520, gap = 12
    const left = Math.max(8, Math.min(fabPos.left + 56 - W, window.innerWidth - 8 - W))
    let top = fabPos.top - gap - H
    if (top < 8) top = Math.min(fabPos.top + 56 + gap, window.innerHeight - 8 - H)
    return { left, top: Math.max(8, top) }
  }

  // ── Persist mode + ticketId ─────────────────────────────
  useEffect(() => { localStorage.setItem(LS_MODE, mode) }, [mode])
  useEffect(() => {
    if (ticketId) localStorage.setItem(LS_TICKET, ticketId)
    else          localStorage.removeItem(LS_TICKET)
  }, [ticketId])

  // ── On mount: restore messages if in live/ended mode ────
  useEffect(() => {
    if (!ticketId || (mode !== 'live' && mode !== 'ended')) return
    supabase.from('support_messages')
      .select('*')
      .eq('ticket_id', ticketId)
      .order('created_at', { ascending: true })
      .then(({ data }) => {
        if (!data?.length) return
        setMessages(
          data.map(m => ({
            id:        m.id,
            role:      m.sender_type === 'user' ? 'user' : 'ada',
            text:      m.message,
            agentName: m.sender_type === 'agent' ? m.sender_name : undefined,
            isSystem:  m.sender_type === 'system',
          }))
        )
      })
  }, []) // mount only — intentional

  // ── Broadcast subscription for live mode ────────────────
  // Uses Supabase Broadcast (pure WebSocket) — no RLS, instant delivery.
  useEffect(() => {
    if (!ticketId || mode !== 'live') return

    const ch = supabase.channel(`ticket-${ticketId}`)
      .on('broadcast', { event: 'message' }, ({ payload }) => {
        // Ignore own messages (already shown optimistically)
        if (payload.sender_type === 'user') return

        setMessages(p => {
          if (p.some(m => m.id === payload.id)) return p // deduplicate
          return [...p, {
            id:        payload.id,
            role:      'ada',
            text:      payload.message,
            agentName: payload.sender_type === 'agent' ? payload.sender_name : undefined,
            isSystem:  payload.sender_type === 'system',
          }]
        })

        if (!open) setUnread(c => c + 1)

        // Agent resolved the chat
        if (payload.sender_type === 'system') setMode('ended')
      })
      .subscribe()

    liveChannelRef.current = ch

    return () => {
      supabase.removeChannel(ch)
      liveChannelRef.current = null
    }
  }, [ticketId, mode]) // excludes `open` — subscription stays alive while widget is closed

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    if (open) {
      setUnread(0)
      setTimeout(() => inputRef.current?.focus(), 280)
    }
  }, [open])

  // ── Open / close ────────────────────────────────────────
  function handleToggle() {
    if (open) {
      setOpen(false)
      if (mode === 'intro') { setMessages([]); setInput('') }
    } else {
      setOpen(true)
      if (mode === 'intro' && messages.length === 0) {
        setMessages([{ id: 1, role: 'ada', text: OPENING_TEXT }])
      }
    }
  }

  // ── Intro: chat with Ada (Groq AI). "Talk to a human" escalates to live. ──
  async function sendAiMessage(text) {
    const trimmed = text.trim()
    if (!trimmed || mode !== 'intro' || aiLoading) return
    const userMsg = { id: Date.now(), role: 'user', text: trimmed }
    setMessages(p => [...p, userMsg])
    setInput('')
    setAiLoading(true)
    const convo = [...messages, userMsg]
      .filter(m => !m.isSystem)
      .map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }))
    try {
      const { data, error } = await supabase.functions.invoke('okoiya-support', {
        body: { messages: convo, context: { coupleName: wedding?.partner1_name || '', isPremium } },
      })
      if (error) throw error
      setMessages(p => [...p, { id: Date.now() + 1, role: 'ada', text: data?.reply || 'Tap “Talk to a human” below and our team will help. 💛' }])
    } catch {
      setMessages(p => [...p, { id: Date.now() + 1, role: 'ada', text: "I'm having trouble right now — tap “Talk to a human” below and our team will help. 💛" }])
    }
    setAiLoading(false)
  }

  // ── Live message send (Broadcast) ───────────────────────
  async function sendLiveMessage(text) {
    const trimmed = text.trim()
    if (!trimmed || !ticketId) return

    const msgId = uuid()

    // Show immediately
    setMessages(p => [...p, { id: msgId, role: 'user', text: trimmed }])
    setInput('')

    const payload = {
      id:          msgId,
      ticket_id:   ticketId,
      sender_type: 'user',
      sender_name: wedding?.partner1_name || user?.email || 'Customer',
      message:     trimmed,
      created_at:  new Date().toISOString(),
    }

    // Persist + broadcast simultaneously
    await supabase.from('support_messages').insert(payload)
    liveChannelRef.current?.send({ type: 'broadcast', event: 'message', payload })
  }

  // ── Connect to a human support agent ────────────────────
  async function connectToSupport(firstMessage) {
    setMode('connecting')
    setMessages(p => [...p, {
      id: Date.now(), role: 'ada',
      text: "Connecting you to our support team now. Someone will be with you shortly ✨",
    }])

    const coupleName = wedding
      ? `${wedding.partner1_name} & ${wedding.partner2_name}`
      : (user?.email || 'Customer')

    const { data: ticket, error } = await supabase.from('support_tickets').insert({
      wedding_id:  wedding?.id || null,
      user_id:     user?.id,
      couple_name: coupleName,
      status:      'open',
    }).select().single()

    if (error || !ticket) {
      setMode('intro')
      setMessages(p => [...p, { id: Date.now(), role: 'ada', text: "Sorry, I couldn't connect you right now. Please email support@okoiya.com 💛" }])
      return
    }

    setTicketId(ticket.id)

    // Persist the customer's first message so the agent sees it (already shown in the widget)
    if (firstMessage?.trim()) {
      supabase.from('support_messages').insert({
        ticket_id: ticket.id, sender_type: 'user', sender_name: coupleName, message: firstMessage.trim(),
      }).then(() => {})
    }

    setMode('live')
    setMessages(p => [...p, {
      id: Date.now(), role: 'ada',
      text: "You're now connected to our support team. A real person will reply here — we typically respond within a few minutes.",
    }])

    // Email agents (fire and forget)
    notifyAgents(coupleName)
  }

  async function notifyAgents(coupleName) {
    try {
      const { data: agents } = await supabase.from('cs_agents').select('email')
      if (!agents?.length) return
      const { data: { session } } = await supabase.auth.getSession()
      const url = import.meta.env.VITE_SUPABASE_URL + '/functions/v1/send-email'
      agents.forEach(a => {
        fetch(url, {
          method:  'POST',
          headers: { 'Authorization': 'Bearer ' + (session?.access_token || ''), 'Content-Type': 'application/json' },
          body:    JSON.stringify({ type: 'support_alert', data: { agentEmail: a.email, coupleName } }),
        }).catch(() => {})
      })
    } catch (_) {}
  }

  // ── Leave the human queue and continue with Cupid ───────
  // Lets a couple bail out if no agent has replied yet, without losing the chat.
  async function backToCupid() {
    const tid = ticketId
    if (tid) {
      // Best-effort: close the ticket so it leaves the agents' queue, and let
      // any agent who has it open know the customer stepped away.
      const sys = { id: uuid(), ticket_id: tid, sender_type: 'system', sender_name: 'System', message: 'The customer returned to the Cupid assistant.', created_at: new Date().toISOString() }
      supabase.from('support_messages').insert(sys).then(() => {})
      liveChannelRef.current?.send({ type: 'broadcast', event: 'message', payload: sys })
      supabase.from('support_tickets').update({ status: 'resolved', closed_at: new Date().toISOString(), close_note: 'Customer returned to the assistant' }).eq('id', tid).then(() => {})
    }
    localStorage.removeItem(LS_TICKET)
    localStorage.setItem(LS_MODE, 'intro')
    setTicketId(null)
    setMode('intro')
    setMessages([{ id: Date.now(), role: 'ada', text: "No problem — I'm right here. 💛 What would you like help with?" }])
    setInput('')
  }

  // ── Reset to intro ──────────────────────────────────────
  function resetChat() {
    localStorage.removeItem(LS_TICKET)
    localStorage.setItem(LS_MODE, 'intro')
    setMode('intro')
    setTicketId(null)
    setMessages([{ id: 1, role: 'ada', text: OPENING_TEXT }])
    setInput('')
  }

  const isLive    = mode === 'live'
  const isEnded   = mode === 'ended'
  const isConnect = mode === 'connecting'
  const isIntro   = mode === 'intro'

  return (
    <>
      <style>{`
        .cupid-window { animation:cupidOpen 0.25s cubic-bezier(0.34,1.56,0.64,1) both; transform-origin:bottom right; }
        @keyframes cupidOpen { from{transform:scale(0.85);opacity:0} to{transform:scale(1);opacity:1} }
        .cupid-msgs::-webkit-scrollbar{width:4px}
        .cupid-msgs::-webkit-scrollbar-track{background:transparent}
        .cupid-msgs::-webkit-scrollbar-thumb{background:#C4826A;border-radius:4px}
        @keyframes onlinePulse{0%,100%{opacity:1}50%{opacity:.45}}
        .human-btn:hover{opacity:.85 !important}
        @media(max-width:768px){.cupid-window{width:calc(100vw - 48px) !important;height:75vh !important;bottom:80px !important;right:24px !important;left:auto !important;top:auto !important}}
      `}</style>

      {/* ── FAB ───────────────────────────────────── */}
      <button
        onMouseDown={fabMouseDown}
        onTouchStart={fabTouchStart}
        onTouchMove={fabTouchMove}
        onTouchEnd={fabTouchEnd}
        onClick={() => { if (!didDrag.current && !isTouchTap.current) handleToggle() }}
        title={open ? 'Close chat' : 'Chat with Cupid · drag to move'}
        style={{ position:'fixed', left:fabPos.left, top:fabPos.top, zIndex:50, width:56, height:56, borderRadius:'50%', background:'#FFFDFA', border:'1.5px solid rgba(196,130,106,0.45)', cursor:'grab', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 20px rgba(196,130,106,0.30)', transition:'box-shadow 0.15s', userSelect:'none', touchAction:'none' }}
        onMouseEnter={e=>{e.currentTarget.style.boxShadow='0 6px 28px rgba(196,130,106,0.45)'}}
        onMouseLeave={e=>{e.currentTarget.style.boxShadow='0 4px 20px rgba(196,130,106,0.30)'}}>
        {open
          ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#C4826A" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          : <CupidIcon size={42} variant="gradient" />
        }
        {!open && unread > 0 && (
          <div style={{ position:'absolute', top:0, right:0, width:18, height:18, borderRadius:'50%', background:'#EF4444', border:'2px solid white', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <span style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.6rem', fontWeight:700, color:'white', lineHeight:1 }}>{unread > 9 ? '9+' : unread}</span>
          </div>
        )}
      </button>

      {/* ── Chat window ───────────────────────────── */}
      {open && (
        <div className="cupid-window" style={{ position:'fixed', left:windowPos().left, top:windowPos().top, zIndex:50, width:340, height:520, display:'flex', flexDirection:'column', borderRadius:'1.25rem', boxShadow:'0 16px 56px rgba(0,0,0,0.2)', overflow:'hidden' }}>

          {/* Header */}
          <div style={{ background:isLive||isEnded?'#1A1A1A':'#C4826A', height:64, padding:'0 1.25rem', display:'flex', alignItems:'center', gap:'0.75rem', flexShrink:0, borderRadius:'1.25rem 1.25rem 0 0', transition:'background 0.3s' }}>
            <div style={{ width:38, height:38, borderRadius:'50%', background:'rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {isLive||isEnded
                ? <span style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:700, fontSize:'1.1rem', color:'white' }}>🙋</span>
                : <CupidIcon size={30} variant="white" />}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:600, fontSize:'0.95rem', color:'white', margin:0, lineHeight:1.2 }}>
                {isLive?'Live Support':isEnded?'Chat Ended':'Cupid · Okoiya'}
              </p>
              <div style={{ display:'flex', alignItems:'center', gap:'0.35rem', marginTop:'0.15rem' }}>
                <div style={{ width:7, height:7, borderRadius:'50%', background:isConnect?'#FBBF24':'#4ADE80', animation:'onlinePulse 2s ease-in-out infinite', flexShrink:0 }}/>
                <span style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.72rem', color:'rgba(255,255,255,0.82)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {isConnect?'Connecting…':isLive?'Support team · Online':isEnded?'Conversation closed':'AI assistant · instant answers'}
                </span>
              </div>
            </div>
            <button onClick={handleToggle} style={{ background:'none', border:'none', cursor:'pointer', padding:4, lineHeight:0, flexShrink:0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          {/* Messages */}
          <div className="cupid-msgs" style={{ flex:1, overflowY:'auto', padding:'1rem', background:'white', display:'flex', flexDirection:'column', gap:'0.625rem' }}>
            {messages.map(msg => {
              if (msg.isSystem) return (
                <div key={msg.id} style={{ textAlign:'center', margin:'0.25rem 0' }}>
                  <span style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.72rem', color:'#9B9B9B', background:'#F5F0EA', borderRadius:100, padding:'0.3rem 0.875rem', display:'inline-block' }}>
                    {msg.text}
                  </span>
                </div>
              )
              return (
                <div key={msg.id}>
                  <div style={{ display:'flex', justifyContent:msg.role==='user'?'flex-end':'flex-start' }}>
                    <div style={{ maxWidth:'82%', padding:'0.75rem 1rem', borderRadius:msg.role==='user'?'1rem 0 1rem 1rem':'0 1rem 1rem 1rem', background:msg.role==='user'?'#C4826A':'#F5F0EA', fontFamily:'Nunito Sans,sans-serif', fontSize:'0.875rem', color:msg.role==='user'?'white':'#1A1A1A', lineHeight:1.65, wordBreak:'break-word' }}>
                      {msg.agentName && <span style={{ display:'block', fontFamily:'Nunito Sans,sans-serif', fontSize:'0.65rem', color:'#9B9B9B', marginBottom:'0.2rem', fontWeight:700 }}>{msg.agentName.split('@')[0]}</span>}
                      <span style={{ whiteSpace:'pre-wrap' }}>{msg.text}</span>
                    </div>
                  </div>
                </div>
              )
            })}

            {aiLoading && (
              <div style={{ display:'flex', justifyContent:'flex-start' }}>
                <div style={{ padding:'0.6rem 0.95rem', borderRadius:'0 1rem 1rem 1rem', background:'#F5F0EA', fontFamily:'Nunito Sans,sans-serif', fontSize:'0.8rem', color:'#9B9B9B' }}>
                  Cupid is typing…
                </div>
              </div>
            )}

            {isIntro && (
              <div style={{ textAlign:'center', marginTop:'0.25rem' }}>
                <button className="human-btn" onClick={() => connectToSupport()}
                  style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.78rem', fontWeight:600, color:'#C4826A', background:'white', border:'1px solid #C4826A', borderRadius:100, padding:'0.5rem 1.125rem', cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'0.4rem' }}>
                  🙋 Talk to a human
                </button>
              </div>
            )}

            {(isLive || isConnect) && (
              <div style={{ textAlign:'center', marginTop:'0.4rem' }}>
                <button onClick={backToCupid}
                  style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.74rem', color:'#9B9B9B', background:'none', border:'none', cursor:'pointer', textDecoration:'underline', padding:'0.25rem' }}>
                  ← Continue with Cupid instead
                </button>
              </div>
            )}

            {isEnded && (
              <div style={{ textAlign:'center', marginTop:'0.5rem' }}>
                <button onClick={resetChat}
                  style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.78rem', color:'#C4826A', background:'white', border:'1px solid #C4826A', borderRadius:100, padding:'0.45rem 1rem', cursor:'pointer' }}>
                  Start new chat
                </button>
              </div>
            )}

            <div ref={bottomRef}/>
          </div>

          {/* Input */}
          <div style={{ background:'white', borderTop:'1px solid #E5E0D8', padding:'0.75rem 1rem 0.625rem', flexShrink:0 }}>
            {!isEnded && (
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <input
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); isLive?sendLiveMessage(input):sendAiMessage(input) } }}
                  placeholder={isLive?'Message support…':aiLoading?'Cupid is typing…':'Ask Cupid anything…'}
                  disabled={isConnect||aiLoading}
                  style={{ flex:1, fontFamily:'Nunito Sans,sans-serif', fontSize:'0.875rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.6rem 1rem', outline:'none', transition:'border-color 0.15s', background:(isConnect||aiLoading)?'#FAFAF8':'white' }}
                  onFocus={e => e.target.style.borderColor='#C4826A'}
                  onBlur={e  => e.target.style.borderColor='#E5E0D8'}
                />
                <button onClick={() => isLive?sendLiveMessage(input):sendAiMessage(input)}
                  disabled={isConnect||aiLoading||!input.trim()}
                  style={{ fontFamily:'Nunito Sans,sans-serif', fontWeight:600, fontSize:'0.78rem', color:'white', background:(isConnect||aiLoading||!input.trim())?'#D4C4BC':'#C4826A', border:'none', borderRadius:100, padding:'0.6rem 1.1rem', cursor:(isConnect||aiLoading||!input.trim())?'default':'pointer', transition:'background 0.15s', flexShrink:0 }}>
                  SEND
                </button>
              </div>
            )}
            <p style={{ fontFamily:'Nunito Sans,sans-serif', fontSize:'0.68rem', color:'#BCBCBC', textAlign:'center', margin:'0.5rem 0 0' }}>
              Okoiya Support · we typically reply within a few minutes
            </p>
          </div>
        </div>
      )}
    </>
  )
}
